/**
 * useLang - global language context for the SPA.
 *
 * Default locale rule (per DECISIONS s2026-06-12-GEO-DEFAULT-LANG, supersedes
 * partial 2026-05-05-i18n-locale-default-rule buoc 2):
 *   1. Read `localStorage.asklaw_lang` (user explicit override). Values: 'vi' | 'en'.
 *      CHI tinh la override khi co marker `asklaw_lang_set='1'` (ghi boi setLang /
 *      LangSwitcher). Gia tri legacy khong marker -> bo qua (BUG-2026-06-12-LANG-POISON).
 *   2. Else geo country (localStorage.asklaw_geo cache, TTL 7 ngay, nguon
 *      GET /cdn-cgi/trace dong `loc=XX`): 'VN' -> 'vi', khac -> 'en'.
 *   3. Else inspect `navigator.language` - if startsWith 'vi' -> 'vi', else 'en'
 *      (fallback khi geo unknown: adblock / localhost dev / trace fail).
 *   4. SSR/no-window guard -> 'vi' (VN-first market default).
 *
 * `asklaw_lang` CHI duoc ghi boi hanh dong user chu dong (LangSwitcher / setLang).
 * Route-driven sync (landing `/` EN vs `/vi/` VN) PHAI dung `setLangEphemeral`
 * de khong poison override (bug 2026-06-12: visitor VN vao `/` bi ghi 'en'
 * vinh vien -> toan bo product surface hien EN).
 *
 * Persists user override to localStorage on toggle and dispatches a
 * same-tab `asklaw:lang` event so other subscribers re-render. Cross-tab
 * `storage` events also propagate.
 *
 * UPL implication: components rendering legal disclaimers MUST consume
 * this context and render single-locale text. Mixed VN+EN disclaimers
 * weaken "user understood disclaimer" presumption (LEGAL-DIY Sec.3).
 */

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Lang = 'vi' | 'en';

const LANG_KEY = 'asklaw_lang';
// '1' chi khi `asklaw_lang` duoc ghi boi hanh dong user chu dong (LangSwitcher /
// toggle qua setLang). Phan biet "user chon" voi "he thong tu luu".
// Bug BUG-2026-06-12-LANG-POISON: build < 2026-06-12 ghi URL-derived lang vao
// `asklaw_lang` (landing-editorial setLang on mount) nhu the la explicit override
// -> overload 1 key 2 nghia. Marker nay tu-migrate: gia tri legacy KHONG co
// marker -> coi nhu khong phai explicit -> geo/navigator van quyet dinh.
const LANG_EXPLICIT_KEY = 'asklaw_lang_set';
const LANG_EVENT = 'asklaw:lang';
const GEO_KEY = 'asklaw_geo';
const GEO_EVENT = 'asklaw:geo';
const GEO_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 ngay
const GEO_FETCH_TIMEOUT_MS = 3000;

/**
 * Pure: chi tra lang khi co marker explicit (`asklaw_lang_set === '1'`).
 * Gia tri legacy khong marker -> null (tu-migrate, KHONG xoa data). Exported
 * cho test khong can jsdom. Per BUG-2026-06-12-LANG-POISON.
 */
export function pickStoredLang(rawLang: string | null, explicitFlag: string | null): Lang | null {
  if (explicitFlag !== '1') return null;
  return rawLang === 'vi' || rawLang === 'en' ? rawLang : null;
}

function readStoredLang(): Lang | null {
  try {
    return pickStoredLang(
      window.localStorage.getItem(LANG_KEY),
      window.localStorage.getItem(LANG_EXPLICIT_KEY),
    );
  } catch {
    return null;
  }
}

/** Explicit user lang override, if any (exported for HomeGate geo redirect). */
export function getStoredLang(): Lang | null {
  if (typeof window === 'undefined') return null;
  return readStoredLang();
}

/** Cached geo country (ISO 3166-1 alpha-2) or null when missing/stale/invalid. */
function readGeoCountry(): string | null {
  try {
    const raw = window.localStorage.getItem(GEO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { c?: unknown; ts?: unknown };
    if (typeof parsed?.c !== 'string' || typeof parsed?.ts !== 'number') return null;
    if (!/^[A-Z]{2}$/.test(parsed.c)) return null;
    if (Date.now() - parsed.ts > GEO_TTL_MS) return null;
    return parsed.c;
  } catch {
    return null;
  }
}

/** Parse `loc=XX` line tu CF /cdn-cgi/trace body. Exported for tests. */
export function parseTraceCountry(text: string): string | null {
  const m = /(?:^|\n)loc=([A-Z]{2})(?:\n|$)/.exec(text);
  return m ? m[1] : null;
}

/**
 * Fetch country tu CF edge /cdn-cgi/trace (co san tren moi zone CF-proxied,
 * khong ton Worker invocation). Cache vao localStorage + dispatch GEO_EVENT.
 * Fail-silent (null): adblock, localhost dev, timeout - caller fallback navigator.
 */
export async function refreshGeoCountry(): Promise<string | null> {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return null;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), GEO_FETCH_TIMEOUT_MS);
    const res = await fetch('/cdn-cgi/trace', { signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const country = parseTraceCountry(await res.text());
    if (!country) return null;
    try {
      window.localStorage.setItem(GEO_KEY, JSON.stringify({ c: country, ts: Date.now() }));
    } catch {
      // ignore - in-memory event still fires
    }
    window.dispatchEvent(new Event(GEO_EVENT));
    return country;
  } catch {
    return null;
  }
}

/**
 * Pure resolver cua default-lang priority (DECISIONS s2026-06-12-GEO-DEFAULT-LANG).
 * Exported rieng de unit test khong can DOM/jsdom gymnastics.
 */
export function resolveLangFrom(input: {
  stored: Lang | null;
  geoCountry: string | null;
  navLang: string | null;
}): Lang {
  if (input.stored) return input.stored;
  if (input.geoCountry) return input.geoCountry.toUpperCase() === 'VN' ? 'vi' : 'en';
  if (input.navLang) return input.navLang.toLowerCase().startsWith('vi') ? 'vi' : 'en';
  return 'vi';
}

/**
 * Detect SSG prerender context.
 *
 * vite-react-ssg uses jsdom + sets `globalThis.$jsdom` (NOT `window.$jsdom`)
 * and the jsdom navigator.userAgent is "Node.js/<v>" (NOT "jsdom"). During
 * SSG, jsdom's navigator.language defaults to 'en-US' regardless of
 * asklaw's VN-first stance, leaking EN copy into the static HTML snapshot
 * and triggering React hydration error #418 once the client re-renders
 * with the correct stored/detected locale.
 *
 * Treat any node/jsdom context as SSR -> default to 'vi'.
 */
function isSsrLikeContext(): boolean {
  if (typeof window === 'undefined') return true;
  // vite-react-ssg jsdomGlobal injects `$jsdom` on globalThis.
  const g = globalThis as unknown as { $jsdom?: unknown };
  if (typeof g.$jsdom !== 'undefined') return true;
  // jsdom navigator.userAgent in vite-react-ssg defaults to "Node.js/<v>".
  // Real browsers always include "Mozilla". If we see Node.js UA or no
  // Mozilla token, treat as SSR.
  if (typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string') {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('jsdom') || ua.includes('node.js')) return true;
    if (!ua.includes('mozilla')) return true;
  }
  return false;
}

/** Default-lang resolver - exposed for tests + non-React consumers. */
export function resolveDefaultLang(): Lang {
  if (isSsrLikeContext()) return 'vi';
  return resolveLangFrom({
    stored: readStoredLang(),
    geoCountry: readGeoCountry(),
    navLang: typeof navigator !== 'undefined' ? navigator.language || null : null,
  });
}

type LangContextValue = {
  lang: Lang;
  setLang: (next: Lang) => void;
  /**
   * Update lang state KHONG ghi localStorage override. Danh cho route-driven
   * sync (landing `/` EN / `/vi/` VN). User explicit choice van di qua setLang.
   */
  setLangEphemeral: (next: Lang) => void;
  toggleLang: () => void;
};

const LangContext = createContext<LangContextValue | null>(null);

// Gia tri lang ma SSG/SSR bake ra HTML (isSsrLikeContext() -> 'vi'). First client
// render PHAI = gia tri nay de khop SSG header text -> tranh hydration mismatch
// React #418 (BUG-2026-06-12-LANG-HYDRATION-418). resolveDefaultLang() chay client
// co the tra 'en' (navigator en-US) -> neu dung lam initial state se lech SSG header
// (bake 'vi') -> #418; khi #418 trung voi geo redirect client-Navigate -> tree blank.
const SSR_BAKED_LANG: Lang = 'vi';

export function LangProvider({ children }: { children: ReactNode }) {
  // Khoi tao = gia tri SSG bake (khong goi resolveDefaultLang o initial). Resolve
  // locale that su o mount effect ben duoi (sau hydration commit) -> 0 mismatch.
  const [lang, setLangState] = useState<Lang>(SSR_BAKED_LANG);

  useEffect(() => {
    const sync = () => {
      setLangState(resolveDefaultLang());
    };
    // Resolve locale that su NGAY sau hydration (post-commit, khong gay #418).
    sync();
    const onStorage = (e: StorageEvent) => {
      if (e.key === LANG_KEY || e.key === LANG_EXPLICIT_KEY || e.key === GEO_KEY || e.key === null) sync();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(LANG_EVENT, sync);
    window.addEventListener(GEO_EVENT, sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(LANG_EVENT, sync);
      window.removeEventListener(GEO_EVENT, sync);
    };
  }, []);

  // Geo bootstrap: chi fetch khi cache missing/stale. GEO_EVENT tu
  // refreshGeoCountry kich sync() o tren -> re-resolve default (explicit
  // override van thang vi resolveDefaultLang uu tien stored truoc geo).
  useEffect(() => {
    if (isSsrLikeContext()) return;
    if (readGeoCountry() !== null) return;
    void refreshGeoCountry();
  }, []);

  // Mirror to <html lang> for accessibility + screen readers.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  // setLang = explicit user choice (LangSwitcher / toggle). Ghi ca marker
  // `asklaw_lang_set='1'` de readStoredLang coi day la override that. Route-driven
  // sync (landing) PHAI dung setLangEphemeral (khong ghi) - tranh poison.
  const setLang = useCallback((next: Lang) => {
    try {
      window.localStorage.setItem(LANG_KEY, next);
      window.localStorage.setItem(LANG_EXPLICIT_KEY, '1');
    } catch {
      // ignore - still update in-memory state
    }
    setLangState(next);
    window.dispatchEvent(new Event(LANG_EVENT));
  }, []);

  // KHONG ghi localStorage + KHONG dispatch LANG_EVENT (event se trigger sync()
  // re-resolve default va clobber gia tri ephemeral nay).
  const setLangEphemeral = useCallback((next: Lang) => {
    setLangState(next);
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === 'vi' ? 'en' : 'vi');
  }, [lang, setLang]);

  const value = useMemo<LangContextValue>(
    () => ({ lang, setLang, setLangEphemeral, toggleLang }),
    [lang, setLang, setLangEphemeral, toggleLang],
  );

  return createElement(LangContext.Provider, { value }, children);
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) {
    // Defensive fallback - allows component to render outside provider in
    // isolated tests. Returns ephemeral (no persistence) state.
    return {
      lang: resolveDefaultLang(),
      setLang: () => {
        /* noop outside provider */
      },
      setLangEphemeral: () => {
        /* noop outside provider */
      },
      toggleLang: () => {
        /* noop outside provider */
      },
    };
  }
  return ctx;
}

/**
 * Geo country hien biet (cache localStorage, cap nhat khi refreshGeoCountry
 * resolve). null = chua biet (SSR / cache stale / trace fail).
 */
export function useGeoCountry(): string | null {
  const [country, setCountry] = useState<string | null>(() =>
    isSsrLikeContext() ? null : readGeoCountry(),
  );
  useEffect(() => {
    const sync = () => setCountry(readGeoCountry());
    sync();
    window.addEventListener(GEO_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(GEO_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return country;
}
